import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  MessageSquareText,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  projectsApi,
  type RaGenerationItem,
  type RaGenerationProfile,
  type RaGenerationRun,
  type RaGenerationState,
} from '@/lib/projectsApi'
import { RaDraftPreviewPanel } from '@/components/projects/RaDraftPreviewPanel'
import { useTranslation } from 'react-i18next'

interface Props {
  projectId: string
  projectHull: string
  initialProfileCode: string | null
  canEdit: boolean
}

const POLLING_STATUSES = new Set(['queued', 'preparing', 'review_required', 'finalizing'])
const DEFAULT_MAX_COST_USD = 5

function unwrapRun(value: RaGenerationRun | { run: RaGenerationRun }): RaGenerationRun {
  return 'run' in value ? value.run : value
}

function stateRun(value: RaGenerationState | null): RaGenerationRun | null {
  return value?.run ?? value?.active_run ?? value?.latest_run ?? null
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

function profileLabel(profile: RaGenerationProfile): string {
  const name = profile.display_name ? ` · ${profile.display_name}` : ''
  return `${profile.class_society} · ${profile.profile_code}${name}`
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ')
}

function statusTone(status: string): string {
  if (['ready', 'prepared', 'reviewed', 'finalized', 'complete', 'completed'].includes(status)) {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  if (['failed', 'blocked', 'cancelled'].includes(status)) {
    return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  }
  if (['queued', 'preparing', 'finalizing', 'pending'].includes(status)) {
    return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
  }
  return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
}

export const RaGenerationWorkspace: React.FC<Props> = ({
  projectId,
  projectHull,
  initialProfileCode,
  canEdit,
}) => {
  const { t } = useTranslation()
  const [state, setState] = useState<RaGenerationState | null>(null)
  const [profileCode, setProfileCode] = useState(initialProfileCode ?? '')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'start' | 'retry' | 'finalize' | 'download' | 'instruction' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [instruction, setInstruction] = useState('')
  const [instructionResult, setInstructionResult] = useState<string | null>(null)
  const loadSequence = useRef(0)
  const runSequence = useRef(0)

  const load = useCallback(async (code?: string | null, silent = false) => {
    const sequence = ++loadSequence.current
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const next = await projectsApi.getRaGeneration(projectId, code)
      if (sequence !== loadSequence.current) return
      setState(next)
      const selected = code || next.profile?.profile_code || next.profiles?.[0]?.profile_code || ''
      setProfileCode(selected)
    } catch (err) {
      if (sequence === loadSequence.current) setError(errorMessage(err, t('raGeneration.requestFailed')))
    } finally {
      if (sequence === loadSequence.current && !silent) setLoading(false)
    }
  }, [projectId, t])

  useEffect(() => {
    loadSequence.current++
    runSequence.current++
    setState(null)
    setProfileCode(initialProfileCode ?? '')
    setBusy(null)
    setError(null)
    void load(initialProfileCode)
  }, [initialProfileCode, load, projectId])

  const run = stateRun(state)
  const runId = run?.id ?? null
  const shouldPoll = Boolean(run && POLLING_STATUSES.has(run.status))
  useEffect(() => {
    if (!runId || !shouldPoll) return
    let cancelled = false
    const sequence = ++runSequence.current
    const tick = async () => {
      try {
        const next = unwrapRun(await projectsApi.getRaGenerationRun(runId))
        if (cancelled || sequence !== runSequence.current) return
        setState(previous => previous ? { ...previous, run: next } : previous)
        setError(null)
      } catch (err) {
        if (!cancelled && sequence === runSequence.current) setError(errorMessage(err, t('raGeneration.requestFailed')))
      }
    }
    const timer = window.setInterval(() => { void tick() }, 2500)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [runId, shouldPoll, t])

  const profiles = useMemo(() => {
    const options = [...(state?.profiles ?? [])]
    if (state?.profile && !options.some(option => option.profile_code === state.profile?.profile_code)) {
      options.push(state.profile)
    }
    return options.sort((a, b) => {
      const lr = Number(b.class_society === 'LR') - Number(a.class_society === 'LR')
      return lr || (a.position ?? 99) - (b.position ?? 99) || profileLabel(a).localeCompare(profileLabel(b))
    })
  }, [state?.profile, state?.profiles])

  const groupedItems = useMemo(() => {
    const groups = new Map<string, Map<string, RaGenerationItem[]>>()
    for (const item of run?.items ?? []) {
      const route = item.routing || 'unrouted'
      const statuses = groups.get(route) ?? new Map<string, RaGenerationItem[]>()
      const items = statuses.get(item.status) ?? []
      items.push(item)
      statuses.set(item.status, items)
      groups.set(route, statuses)
    }
    return [...groups.entries()]
  }, [run?.items])

  const reviewItems = useMemo(
    () => (run?.items ?? []).filter(item => item.ra_draft_id),
    [run?.items],
  )
  const failedItems = useMemo(
    () => (run?.items ?? []).filter(item => item.status === 'failed'),
    [run?.items],
  )
  const canFinalize = Boolean(
    run?.status === 'review_required'
    && run.items.length > 0
    && run.items.every(item => ['approved', 'target', 'skipped', 'finalized'].includes(item.status))
    && run.items.some(item => ['approved', 'target', 'finalized'].includes(item.status))
  )
  const canOfferGeneration = !run || run.status === 'ready' || run.status === 'cancelled'

  const start = async () => {
    if (!profileCode) {
      setError(t('raGeneration.selectProfile'))
      return
    }
    setBusy('start')
    setError(null)
    try {
      const next = unwrapRun(await projectsApi.startRaGeneration(projectId, profileCode, DEFAULT_MAX_COST_USD))
      setState(previous => previous ? { ...previous, run: next } : previous)
    } catch (err) {
      setError(errorMessage(err, t('raGeneration.requestFailed')))
    } finally {
      setBusy(null)
    }
  }

  const retry = async () => {
    if (!run) return
    setBusy('retry')
    setError(null)
    try {
      const next = unwrapRun(await projectsApi.retryRaGeneration(run.id))
      setState(previous => previous ? { ...previous, run: next } : previous)
    } catch (err) {
      setError(errorMessage(err, t('raGeneration.requestFailed')))
    } finally {
      setBusy(null)
    }
  }

  const finalize = async () => {
    if (!run) return
    setBusy('finalize')
    setError(null)
    try {
      const next = unwrapRun(await projectsApi.finalizeRaGeneration(run.id))
      setState(previous => previous ? { ...previous, run: next } : previous)
    } catch (err) {
      setError(errorMessage(err, t('raGeneration.requestFailed')))
    } finally {
      setBusy(null)
    }
  }

  const download = async () => {
    if (!run?.ra_document_id) return
    setBusy('download')
    setError(null)
    try {
      await projectsApi.downloadGeneratedRa(run.ra_document_id, `RA_${projectHull || 'vessel'}.docx`)
    } catch (err) {
      setError(errorMessage(err, t('raGeneration.requestFailed')))
    } finally {
      setBusy(null)
    }
  }

  const applyInstruction = async () => {
    const text = instruction.trim()
    if (!text) return
    setBusy('instruction')
    setError(null)
    setInstructionResult(null)
    try {
      const result = await projectsApi.applyRaInstruction(projectId, text)
      const changed = result.applied.map(row => row.system_name).join(', ')
      setInstructionResult(
        result.status === 'completed'
          ? `${result.summary || t('raGeneration.instructionApplied')}${changed ? ` ${t('raGeneration.updatedSystems', { systems: changed })}` : ''}`
          : result.clarification || t('raGeneration.noRowChanged'),
      )
      if (result.status === 'completed') {
        setInstruction('')
        await load(profileCode, true)
      }
    } catch (err) {
      setError(errorMessage(err, t('raGeneration.requestFailed')))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-700 dark:text-blue-300" />
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('raGeneration.title')}</h3>
          </div>
          <p className="mt-1 max-w-3xl text-[11px] text-slate-500 dark:text-slate-400">
            {t('raGeneration.description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300">
            {t('raGeneration.classProfile')}
            <select
              aria-label={t('raGeneration.classProfile')}
              value={profileCode}
              disabled={loading || shouldPoll}
              onChange={event => {
                const code = event.target.value
                setProfileCode(code)
                void load(code)
              }}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] dark:border-slate-600 dark:bg-slate-900"
            >
              {profiles.length === 0 && <option value="">{t('raGeneration.noProfile')}</option>}
              {profiles.map(profile => (
                <option key={profile.profile_code} value={profile.profile_code}>{profileLabel(profile)}</option>
              ))}
            </select>
          </label>
          <Button
            size="sm"
            variant="outline"
            aria-label={t('raGeneration.refresh')}
            onClick={() => { void load(profileCode) }}
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !state ? (
        <p className="flex items-center gap-2 py-3 text-xs text-slate-500 dark:text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('raGeneration.checkingReadiness')}
        </p>
      ) : state && (
        <>
          <div className="rounded-md bg-slate-50 p-2.5 dark:bg-slate-900/40">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {state.readiness.can_start ? t('raGeneration.ready') : t('raGeneration.notReady')}
              </span>
              <span className="tabular-nums text-slate-500 dark:text-slate-400">{t('common.total')} {state.readiness.total_systems}</span>
              {Object.entries(state.readiness.routings).map(([key, value]) => (
                <span key={key} className="tabular-nums text-slate-500 dark:text-slate-400">{statusLabel(key)} {value}</span>
              ))}
            </div>
            {state.readiness.blockers.length > 0 && (
              <ul className="mt-2 space-y-1 border-t border-slate-200 pt-2 text-[11px] text-amber-700 dark:border-slate-700 dark:text-amber-300">
                {state.readiness.blockers.map((reason, index) => <li key={`${reason}-${index}`}>• {reason}</li>)}
              </ul>
            )}
            {(state.readiness.rows ?? []).some(row => row.reasons.length > 0 || row.warnings.length > 0) && (
              <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto border-t border-slate-200 pt-2 text-[11px] text-slate-600 dark:border-slate-700 dark:text-slate-300">
                {(state.readiness.rows ?? []).filter(row => row.reasons.length > 0 || row.warnings.length > 0).map(row => (
                  <li key={row.screening_system_id}>
                    <span className="font-medium">{row.system_name}</span>
                    <span className="text-slate-400"> · {statusLabel(row.routing)} — </span>
                    {[...row.reasons, ...row.warnings].join('; ')}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {canEdit && (
            <div className="rounded-md border border-slate-200 p-2.5 dark:border-slate-700">
              <div className="mb-1.5 flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-blue-600" />
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{t('raGeneration.instructionTitle')}</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <textarea
                  value={instruction}
                  onChange={event => setInstruction(event.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder={t('raGeneration.instructionPlaceholder')}
                  className="min-h-[56px] flex-1 resize-y rounded-md border border-slate-300 bg-white px-2.5 py-2 text-xs text-slate-800 outline-none focus:border-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                />
                <Button
                  size="sm"
                  onClick={() => { void applyInstruction() }}
                  loading={busy === 'instruction'}
                  disabled={!instruction.trim() || Boolean(run && ['queued', 'preparing', 'finalizing'].includes(run.status))}
                >
                  {t('raGeneration.applyInstruction')}
                </Button>
              </div>
              <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                {t('raGeneration.instructionHelp')}
              </p>
              {instructionResult && <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-300">{instructionResult}</p>}
            </div>
          )}

          {canOfferGeneration && canEdit && (
            <Button onClick={() => { void start() }} loading={busy === 'start'} disabled={!state.readiness.can_start || !profileCode}>
              <FileText className="h-4 w-4" /> {t('raGeneration.generate')}
            </Button>
          )}

          {run && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-2.5 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  {POLLING_STATUSES.has(run.status) && run.status !== 'review_required'
                    ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    : run.status === 'ready'
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  <div>
                    <p className="text-xs font-semibold capitalize text-slate-800 dark:text-slate-100">{statusLabel(run.status)}</p>
                    <p className="text-[10px] text-slate-400">
                      {t('raGeneration.persistedRun', { id: run.id })}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canEdit && (run.status === 'failed' || failedItems.length > 0) && (
                    <Button size="sm" variant="outline" onClick={() => { void retry() }} loading={busy === 'retry'}>
                      <RotateCcw className="h-3.5 w-3.5" /> {t('raGeneration.retryFailed')}
                    </Button>
                  )}
                  {canEdit && run.status === 'review_required' && (
                    <Button size="sm" onClick={() => { void finalize() }} loading={busy === 'finalize'} disabled={!canFinalize}>
                      {t('raGeneration.finalize')}
                    </Button>
                  )}
                  {run.status === 'ready' && (
                    <Button size="sm" onClick={() => { void download() }} loading={busy === 'download'} disabled={!run.ra_document_id}>
                      <Download className="h-3.5 w-3.5" /> {t('raGeneration.download')}
                    </Button>
                  )}
                </div>
              </div>

              {run.error_message && <p className="text-xs text-red-700 dark:text-red-300">{run.error_message}</p>}
              {(run.status === 'queued' || run.status === 'preparing' || run.status === 'finalizing') && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5 dark:border-blue-900/60 dark:bg-blue-950/20">
                  <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px] text-blue-800 dark:text-blue-200">
                    <span>
                      {run.status === 'queued'
                        ? t('raGeneration.waitingQueue', { position: run.queue_position ? ` · ${t('raGeneration.position', { position: run.queue_position })}` : '' })
                        : run.progress?.current_system_name
                          ? t('raGeneration.processingSystem', { name: run.progress.current_system_name })
                          : statusLabel(run.status)}
                    </span>
                    <span className="tabular-nums">
                      {t('raGeneration.percentComplete', { percent: run.progress?.percent ?? 0 })}
                      {(run.progress?.total ?? 0) > 0
                        ? ` · ${run.progress?.completed ?? 0}/${run.progress?.total ?? 0} AI assessments`
                        : ''}
                    </span>
                  </div>
                  <div
                    role="progressbar"
                    aria-label={t('raGeneration.progress')}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={run.progress?.percent ?? 0}
                    className="h-2 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/60"
                  >
                    <div
                      className={`h-full rounded-full bg-blue-600 transition-all duration-500 ${run.status === 'queued' ? 'animate-pulse' : ''}`}
                      style={{ width: `${run.progress?.percent ?? 0}%` }}
                    />
                  </div>
                </div>
              )}
              {run.status === 'review_required' && !canFinalize && (
                <p className="text-[11px] text-amber-700 dark:text-amber-300">{t('raGeneration.finalizeHelp')}</p>
              )}
              {run.status === 'ready' && !run.ra_document_id && (
                <p className="text-[11px] text-amber-700 dark:text-amber-300">{t('raGeneration.noDocumentId')}</p>
              )}

              {groupedItems.length > 0 && (
                <div className="space-y-2">
                  {groupedItems.map(([route, statuses]) => (
                    <div key={route} className="rounded-md border border-slate-200 dark:border-slate-700">
                      <div className="border-b border-slate-100 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700/60 dark:text-slate-400">
                        {t('raGeneration.route')} · {statusLabel(route)}
                      </div>
                      {[...statuses.entries()].map(([status, items]) => (
                        <div key={status} className="border-b border-slate-100 px-2.5 py-2 last:border-b-0 dark:border-slate-700/60">
                          <div className="mb-1 flex items-center gap-2">
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusTone(status)}`}>{statusLabel(status)}</span>
                            <span className="text-[10px] tabular-nums text-slate-400">{items.length}</span>
                          </div>
                          <ul className="space-y-1 text-xs text-slate-700 dark:text-slate-200">
                            {items.map((item, index) => (
                              <li key={item.id ?? `${item.system_name}-${index}`}>
                                <span>{item.system_name}</span>
                                {item.error_message && <span className="ml-2 text-[11px] text-red-600 dark:text-red-300">— {item.error_message}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {run.status === 'review_required' && (
                <div className="space-y-2">
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
                    <p className="font-semibold">{t('raGeneration.humanReview')}</p>
                    {reviewItems.length > 0 ? (
                      <ul className="mt-1 list-disc pl-4">
                        {reviewItems.map((item, index) => <li key={item.id ?? `${item.system_name}-${index}`}>{item.system_name}</li>)}
                      </ul>
                    ) : (
                      <p className="mt-1">{t('raGeneration.noDraftIds')}</p>
                    )}
                  </div>
                  {reviewItems.length > 0 && (
                    <RaDraftPreviewPanel
                      projectId={projectId}
                      initialProfileCode={profileCode || null}
                      canEdit={canEdit}
                      reviewOnly
                      draftIds={reviewItems.flatMap(item => item.ra_draft_id ? [item.ra_draft_id] : [])}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}
